import { ChangeEvent, useState } from 'react'

import { Button, Input } from '@/components/ui'
import { Icon } from '@/common/components/Icon'
import { FormUploadPreview } from '@/core/components/FormUploadPreview'
import FormGroup from '@/core/components/FormGroup'
import { uploadImages } from '@/core/services/upload-image'
import {
  AvatarOptionDto,
  AvatarSelectQuestConfigDto,
} from '@/common/models/gamification'

import { useFestivalLanguages } from '../shared/useFestivalLanguages'

interface AvatarSelectConfigFieldsProps {
  value: AvatarSelectQuestConfigDto
  onChange: (next: AvatarSelectQuestConfigDto) => void
}

/**
 * Editor for an AVATAR_SELECT quest's config: an ordered list of options, each with a photo (240px
 * wide) and one label per festival language. At least 2 options are required; each needs a photo and
 * at least one non-blank label (enforced server-side too).
 */
export const AvatarSelectConfigFields = ({
  value,
  onChange,
}: AvatarSelectConfigFieldsProps) => {
  const { languages } = useFestivalLanguages()
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null)
  const [uploadError, setUploadError] = useState<string | undefined>()

  const options = value.options ?? []

  const setOptions = (next: AvatarOptionDto[]) =>
    onChange({ ...value, options: next })

  const updateOption = (index: number, patch: Partial<AvatarOptionDto>) =>
    setOptions(options.map((o, i) => (i === index ? { ...o, ...patch } : o)))

  const addOption = () =>
    setOptions([...options, { photoRef: '', labels: {} }])

  const removeOption = (index: number) =>
    setOptions(options.filter((_, i) => i !== index))

  const setLabel = (index: number, locale: string, label: string) =>
    updateOption(index, { labels: { ...options[index].labels, [locale]: label } })

  const handlePhotoUpload = async (
    index: number,
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 1_000_000) {
      setUploadError('The photo is too large (max 1 MB)')
      return
    }
    setUploadError(undefined)
    setUploadingIndex(index)
    try {
      const { data } = await uploadImages(file, true, 'GAMIFICATION_AVATAR_OPTION')
      if (data?.photoUrl) updateOption(index, { photoRef: data.photoUrl })
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      setUploadError(
        status === 400 ? 'Photo must be at least 240px wide' : 'Photo upload failed',
      )
    } finally {
      setUploadingIndex(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="text-body-medium text-monochrome-1">
        Options{' '}
        <span className="text-monochrome-2">
          (at least 2 — each needs a photo and a label)
        </span>
      </div>

      {options.length === 0 && (
        <div className="text-sm text-monochrome-2">No options yet.</div>
      )}

      {options.map((option, index) => (
        <div
          key={option.id ?? `new-${index}`}
          className="space-y-4 rounded-lg border border-monochrome-120 p-4"
        >
          <div className="flex items-center justify-between">
            <span className="text-body-medium text-monochrome-1">
              Option {index + 1}
            </span>
            <a
              className="text-accent-3 underline"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                removeOption(index)
              }}
            >
              Remove
            </a>
          </div>

          <FormUploadPreview
            label="Photo (240px wide)"
            previewLabel="Photo"
            isUploading={uploadingIndex === index}
            url={option.photoRef}
            width={96}
            height={96}
            imageName={option.photoRef ? `option ${index + 1}` : undefined}
            onRemove={() => updateOption(index, { photoRef: '' })}
            imageStyle={{ objectFit: 'contain' }}
            control={
              <Input
                multiple={false}
                accept="image/*"
                type="file"
                className="bg-monochrome-5-50"
                onChange={(e) => handlePhotoUpload(index, e)}
              />
            }
          />

          <div className="space-y-3">
            {languages.map((language) => (
              <FormGroup key={language.locale} label={`Label (${language.name})`}>
                <Input
                  value={option.labels?.[language.locale] ?? ''}
                  onChange={(e) => setLabel(index, language.locale, e.target.value)}
                />
              </FormGroup>
            ))}
          </div>
        </div>
      ))}

      {uploadError && (
        <span className="text-xs text-accent-3">{uploadError}</span>
      )}

      <Button variant="primary-bordered" onClick={addOption} type="button">
        Add Option
        <Icon icon="plus-circle" width="18" height="18" viewBox="0 0 18 18" />
      </Button>
    </div>
  )
}
